import {
  type Availability,
  type BlockKind,
  type ExtractionFailure,
  type MaterialSubject,
  type UncertainReason,
} from '@/platform/materials/extraction'

import { NOT_CONNECTED_TEXT } from './ui'

/** Child-facing text for reading real school material (Slice 3). */

export const SEND_TEXT = 'Gửi để đọc'

export const SUBJECT_NAME: Readonly<Record<MaterialSubject, string>> = {
  mathematics: 'Toán',
  english: 'Tiếng Anh',
}

/**
 * Where the file goes when the chosen subject is served only by the local
 * synthetic TEST FIXTURE (server's `syntheticFixtureSubjects`): nowhere
 * outside the development stack, and nothing is really read.
 */
export const SYNTHETIC_FIXTURE_NOTICE_TEXT: readonly string[] = [
  'Đây là bản thử trên máy phát triển, không phải đọc chữ thật.',
  'Trong bản thử nghiệm này, tài liệu được xử lý bằng dữ liệu mô phỏng ngay trong môi trường phát triển. Tài liệu không được gửi tới Google Cloud Document AI.',
  'Nội dung con thấy sau đó là một trang mẫu soạn sẵn, không đọc từ tệp của con.',
]

/** English reading is not offered yet (its lessons are not reviewed). */
export const SUBJECT_UNAVAILABLE_TEXT =
  'Hiện ứng dụng chưa học được tài liệu tiếng Anh. Tài liệu chưa được gửi đi đâu cả. Con có thể gửi tài liệu Toán nhé.'

/** The learner chose a subject the app cannot learn from yet: nothing is sent. */
export const OTHER_SUBJECT_TEXT =
  'Ứng dụng chưa học được môn này từ tài liệu, nên tài liệu sẽ không được gửi đi. Con có thể học bài có sẵn, hoặc chọn tài liệu khác nhé.'

export const CONFIRM_MEANING_TEXT =
  'Xác nhận nghĩa là nội dung giống tài liệu gốc. Không có nghĩa là bài trong tài liệu đã đúng.'

export const NEXT_STEP_TEXT =
  'Bước học tiếp theo từ tài liệu thật (chọn chủ đề, giải thích, luyện tập) chưa có trong phiên bản này.'

export const UNREACHABLE_TEXT =
  'Chưa kết nối được tới chức năng đọc tài liệu. Con kiểm tra mạng rồi thử lại nhé.'

export const NOT_AUTHENTICATED_TEXT =
  'Phiên đăng nhập của bố mẹ đã hết hoặc không hợp lệ. Con nhờ bố mẹ đăng nhập lại rồi thử lại nhé.'

export const SERVICE_ERROR_TEXT =
  'Chức năng đọc tài liệu đang gặp lỗi ở phía ứng dụng. Con thử lại sau một lúc nhé. Nếu vẫn lỗi, con nhờ bố mẹ báo cho người phụ trách.'

export const UNEXPECTED_RESPONSE_TEXT =
  'Ứng dụng nhận được câu trả lời không đúng dạng từ chức năng đọc tài liệu. Con thử lại sau, và nhờ bố mẹ báo cho người phụ trách nếu vẫn lỗi.'

/** Unavailable reasons explained to the child (D-15); entitlement first. */
export const NOT_ENTITLED_TEXT =
  'Gia đình con hiện chưa dùng chức năng gửi tài liệu ở trường. Con vẫn học được các bài có sẵn trong ứng dụng nhé.'

/** D-16: upload needs the learner's own PIN session; it ended or is missing. */
export const SESSION_NEEDED_TEXT = 'Con cần nhập lại mã PIN của con để gửi tài liệu nhé.'

export const CONSENT_REQUIRED_TEXT =
  'Bố mẹ cần đồng ý cho ứng dụng gửi tài liệu tới dịch vụ đọc tài liệu của Google trước. Con nhờ bố mẹ xem nhé.'

export function restrictedText(until: string | null | undefined): string {
  const when =
    until === null || until === undefined
      ? 'sau vài ngày'
      : `từ ${new Date(until).toLocaleString('vi-VN', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}`
  return `Con đã gửi nhiều tệp không phải tài liệu học trong 7 ngày qua, nên tạm thời chưa gửi thêm được. Con gửi lại được ${when}. Các bài có sẵn vẫn học bình thường.`
}

export const RESTRICTED_PARENT_TEXT =
  'Dành cho bố mẹ: ứng dụng đếm số tệp bị từ chối vì không phải tài liệu học trong 7 ngày gần nhất. Từ tệp thứ 6, chức năng gửi tài liệu tạm dừng cho hồ sơ này và tự mở lại khi các lần cũ qua 7 ngày. Lỗi kỹ thuật, ảnh mờ hay tệp ứng dụng chưa chắc chắn không bị tính.'

export const NOT_LEARNING_TEXT =
  'Tệp này có vẻ không phải tài liệu học (sách, phiếu bài tập, vở ghi). Ứng dụng sẽ không dùng nội dung này. Con chọn tài liệu học khác nhé.'

export const NOT_LEARNING_SENT_TEXT =
  'Tệp đã được gửi tới Google để đọc chữ trước khi ứng dụng kiểm tra được. Ứng dụng không lưu tệp và không lưu nội dung này.'

export const UNCERTAIN_MATERIAL_TEXT =
  'Ứng dụng chưa chắc đây là tài liệu Toán ở trường. Con kiểm tra kỹ từng phần với tài liệu gốc, nếu không phải tài liệu học thì chọn tài liệu khác nhé.'

export const RETRYABLE: ReadonlySet<ExtractionFailure> = new Set([
  // After the learner enters their PIN again, the same upload can be retried.
  'learner_session_invalid',
  'network',
  'provider_failed',
  'invalid_response',
  'bad_request',
  'provider_timeout',
])

export const KIND_TEXT: Readonly<Record<BlockKind, string>> = {
  question: 'Câu hỏi',
  instruction: 'Yêu cầu',
  formula: 'Biểu thức',
  text: 'Đoạn chữ',
}

export const REASON_TEXT: Readonly<Record<UncertainReason, string>> = {
  low_confidence:
    'Chữ ở đây có thể bị mờ, nhòe hoặc viết tay nên ứng dụng đọc chưa chắc chắn.',
  no_confidence: 'Dịch vụ đọc không cho biết độ chắc chắn, nên con cần kiểm tra lại.',
  unreadable: 'Ứng dụng không đọc được chỗ này.',
}

export const FAILURE_TEXT: Readonly<
  Record<Exclude<ExtractionFailure, 'cancelled'>, string>
> = {
  unsupported_type:
    'Ứng dụng chưa đọc được loại tệp này. Con chọn ảnh JPG, PNG, WEBP hoặc tệp PDF nhé.',
  empty: 'Tệp này trống. Con chọn tệp khác nhé.',
  too_large: 'Tệp lớn quá (tối đa 10 MB). Con chọn tệp nhỏ hơn nhé.',
  unreadable_document:
    'Ứng dụng không đọc được tệp này (tệp có thể bị hỏng, có mật khẩu hoặc quá nhiều trang). Con chọn tệp khác nhé.',
  no_text_found:
    'Ứng dụng không tìm thấy chữ nào trong tài liệu. Con thử chụp lại rõ hơn, đủ sáng và thẳng trang nhé.',
  network: 'Mất kết nối nên chưa gửi được. Con kiểm tra mạng rồi thử lại nhé.',
  provider_failed: 'Chức năng đọc tài liệu đang gặp lỗi. Con thử lại sau một lúc nhé.',
  invalid_response: 'Chức năng đọc tài liệu đang gặp lỗi. Con thử lại sau một lúc nhé.',
  bad_request: 'Chức năng đọc tài liệu đang gặp lỗi. Con thử lại sau một lúc nhé.',
  provider_timeout:
    'Đọc tài liệu lâu quá nên đã dừng. Con thử lại, hoặc chọn tệp nhỏ hơn, ít trang hơn nhé.',
  not_authenticated: 'Phiên đăng nhập đã hết. Con nhờ bố mẹ đăng nhập lại nhé.',
  learner_session_invalid: SESSION_NEEDED_TEXT,
  provider_not_configured: NOT_CONNECTED_TEXT,
  not_entitled: NOT_ENTITLED_TEXT,
  consent_required: CONSENT_REQUIRED_TEXT,
  restricted: restrictedText(null),
  in_progress: 'Một tài liệu khác đang được đọc. Con đợi một chút rồi thử lại nhé.',
  subject_unavailable: SUBJECT_UNAVAILABLE_TEXT,
  not_learning_material: NOT_LEARNING_TEXT,
}

/** Why reading is not offered, in the child's words. */
export function unavailableText(
  reader: Extract<Availability, { available: false }>,
): string {
  switch (reader.why) {
    case 'not_entitled':
      return NOT_ENTITLED_TEXT
    case 'learner_session_invalid':
      return SESSION_NEEDED_TEXT
    case 'restricted':
      return restrictedText(reader.restrictedUntil)
    case 'consent_required':
      return CONSENT_REQUIRED_TEXT
    case 'unreachable':
      return UNREACHABLE_TEXT
    case 'not_authenticated':
      return NOT_AUTHENTICATED_TEXT
    case 'provider_failed':
      return SERVICE_ERROR_TEXT
    case 'invalid_response':
      return UNEXPECTED_RESPONSE_TEXT
    default:
      return NOT_CONNECTED_TEXT
  }
}
